import { useState } from 'react';

export function IntegrationTimeline() {
  const [copied, setCopied] = useState(false);

  const handleCopyKey = () => {
    navigator.clipboard.writeText('ck_live_8f2a91c2b3d4e5f6a7b8c9d0');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="relative py-28 px-6 lg:px-8 bg-sg border-y border-line">
      {/* Visual connector line */}
      <div className="sc" aria-hidden="true">
        <svg width="2" height="24" viewBox="0 0 2 24">
          <line x1="1" y1="0" x2="1" y2="24" className="sc-line" stroke="#2563EB" strokeOpacity="0.3" strokeWidth="1.5" />
        </svg>
        <span className="sc-node" />
      </div>

      <div className="max-w-3xl mx-auto text-center mb-16">
        <h2 className="text-[32px] sm:text-[40px] font-extrabold tracking-tight text-ink">
          Live in an afternoon.
        </h2>
        <p className="mt-4 text-[16px] text-sub leading-relaxed">
          Six steps from sign-up to your first recorded consent event.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-0">
        {/* Step 1 */}
        <div className="flex gap-5 pb-9 relative">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-ink text-surface flex items-center justify-center text-[12px] font-semibold shrink-0 z-10">
              1
            </div>
            <div className="w-px flex-1 bg-line mt-2"></div>
          </div>
          <div className="pt-1">
            <div className="text-[14.5px] font-semibold text-ink">Create account</div>
            <div className="text-[13px] text-sub mt-0.5">Sign up with your work email.</div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="flex gap-5 pb-9 relative">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-ink text-surface flex items-center justify-center text-[12px] font-semibold shrink-0 z-10">
              2
            </div>
            <div className="w-px flex-1 bg-line mt-2"></div>
          </div>
          <div className="pt-1">
            <div className="text-[14.5px] font-semibold text-ink">Create organisation</div>
            <div className="text-[13px] text-sub mt-0.5">Set up your workspace. Invite your legal and engineering teams.</div>
          </div>
        </div>

        {/* Step 3 */}
        <div className="flex gap-5 pb-9 relative">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-ink text-surface flex items-center justify-center text-[12px] font-semibold shrink-0 z-10">
              3
            </div>
            <div className="w-px flex-1 bg-line mt-2"></div>
          </div>
          <div className="pt-1">
            <div className="text-[14.5px] font-semibold text-ink">Register an application</div>
            <div className="text-[13px] text-sub mt-0.5">Every surface that collects consent is an application. Register each one.</div>
          </div>
        </div>

        {/* Step 4 — Irreversible moment */}
        <div className="flex gap-5 pb-9 relative">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-blue text-white flex items-center justify-center text-[12px] font-semibold shrink-0 z-10">
              4
            </div>
            <div className="w-px flex-1 bg-blue/20 mt-2"></div>
          </div>
          <div className="pt-1 flex-1">
            <div className="text-[14.5px] font-semibold text-ink">Generate API key</div>
            <div className="text-[13px] text-sub mt-0.5 mb-3">
              Scoped, revocable keys per environment. This is when your integration becomes real.
            </div>
            <div className="bg-[#0B0F17] rounded-xl px-4 py-2.5 inline-flex items-center gap-3">
              <span className="mono text-[11px] text-[#6B7280]">ck_live_</span>
              <span className="mono text-[11px] text-[#94A3B8]">••••••••••••8f2a</span>
              <button
                type="button"
                onClick={handleCopyKey}
                className="text-[#4B5563] hover:text-white transition cursor-pointer"
                title="Copy sample key"
              >
                {copied ? (
                  <span className="text-[10px] text-emerald mono">Copied</span>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Step 5 */}
        <div className="flex gap-5 pb-9 relative">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-blue text-white flex items-center justify-center text-[12px] font-semibold shrink-0 z-10">
              5
            </div>
            <div className="w-px flex-1 bg-blue/20 mt-2"></div>
          </div>
          <div className="pt-1">
            <div className="text-[14.5px] font-semibold text-ink">Install SDK</div>
            <div className="text-[13px] text-sub mt-0.5">One command. React, Next.js, Node.js, or vanilla JS.</div>
          </div>
        </div>

        {/* Step 6 */}
        <div className="flex gap-5">
          <div className="w-8 h-8 rounded-full bg-emerald text-white flex items-center justify-center text-[12px] font-semibold shrink-0 mt-1">
            6
          </div>
          <div className="pt-1">
            <div className="text-[14.5px] font-semibold text-ink">Your first consent is recorded.</div>
            <div className="text-[13px] text-sub mt-0.5">
              Timestamped, notice-versioned, immutably logged. Your compliance posture changes permanently.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
